/**
 * @fileoverview Shared permission constants for RBAC
 * @module config/permissions
 *
 * Single source of truth for role hierarchy and default permissions.
 * Used by both client composables and Cloud Functions.
 */

/** Role names for type safety */
export type RoleName = 'viewer' | 'editor' | 'admin' | 'owner'

/**
 * Role hierarchy type - allows known roles with specific types
 * and string access for backward compatibility
 */
export interface RoleHierarchyMap {
  /** Viewer role - basic read access */
  viewer: number
  /** Editor role - read and write access */
  editor: number
  /** Admin role - full tenant management */
  admin: number
  /** Owner role - highest privilege */
  owner: number
  /** Allow string index access for backward compatibility */
  [key: string]: number
}

/**
 * Built-in roles with their hierarchy level
 * Higher level = more permissions
 */
export const ROLE_HIERARCHY: Readonly<RoleHierarchyMap> = {
  viewer: 1,
  editor: 2,
  admin: 3,
  owner: 4
} as const

/**
 * Default permissions per role
 *
 * Permission format: action:resource
 * - read:* - Read all resources
 * - write:* - Write all resources
 * - manage:users - Manage tenant users
 * - manage:settings - Manage tenant settings
 * - * - Full access (owner only)
 */
export const DEFAULT_ROLE_PERMISSIONS: Readonly<Record<RoleName, readonly string[]>> = {
  viewer: ['read:*'],
  editor: ['read:*', 'write:*'],
  admin: ['read:*', 'write:*', 'manage:users', 'manage:settings'],
  owner: ['*']
} as const

/** Default permissions for unknown/invalid roles */
const DEFAULT_FALLBACK_PERMISSIONS: readonly string[] = ['read:*']

/**
 * Get default permissions for a role
 * @param role - The role name
 * @returns Array of permission strings (mutable copy)
 */
export function getDefaultPermissions(role: string): string[] {
  if (isValidRole(role)) {
    return [...DEFAULT_ROLE_PERMISSIONS[role]]
  }
  return [...DEFAULT_FALLBACK_PERMISSIONS]
}

/**
 * Check if a role is valid
 * @param role - The role name to validate
 * @returns Type guard indicating if role is a valid RoleName
 */
export function isValidRole(role: string): role is RoleName {
  return role in ROLE_HIERARCHY
}

/**
 * Get role level for comparison
 * @param role - The role name
 * @returns Role hierarchy level (0 if invalid)
 */
export function getRoleLevel(role: string): number {
  if (isValidRole(role)) {
    return ROLE_HIERARCHY[role]
  }
  return 0
}

/**
 * Check if role1 has higher or equal level than role2
 * @param role1 - First role to compare
 * @param role2 - Second role to compare
 * @returns true if role1 has higher or equal hierarchy level
 */
export function isRoleAtLeast(role1: string, role2: string): boolean {
  return getRoleLevel(role1) >= getRoleLevel(role2)
}
